import isEmpty from 'lodash/isEmpty';
import { ReactNode, createContext, useContext, useState } from 'react';
import { Flow, FlowWithDocument } from '~/types/flow';
import { Money } from '~/types/money';
import { Transaction } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { flowAmount } from '~/utils/flow';
import { hasMixedCurrencies, isZero, maxMoney, negateMoney, newMoney, subtractMoney } from '~/utils/money';

const ReconcileModalContext = createContext<null | {
  transaction: Transaction;
  selectedFlows: SelectedFlow[];
  selectFlow: (flow: FlowWithDocument) => void;
  splitFlow: (flow: FlowWithDocument, amount: Money) => void;
  removeFlow: (val: Flow['id']) => void;
  isSelected: (val: Flow) => boolean;
  getProps: (val: Flow) => undefined | SelectedFlow;
  remainingAmount?: Money;
  isReconciled: boolean;
  hasSelected: boolean;
}>(null);

export interface SelectedFlow {
  flow: FlowWithDocument;
  amount: Money;
  remainingAmount?: Money;
  split: boolean;
}

export const ReconcileModalProvider = ({
  children,
  transaction,
  initialState,
}: {
  children: ReactNode;
  transaction: Transaction;
  initialState?: SelectedFlow[];
}) => {
  const [selectedFlows, setSelectedFlows] = useState<SelectedFlow[]>(initialState ?? []);

  const addFlow = (selected: SelectedFlow) => {
    setSelectedFlows((s) => [selected, ...s.filter((x) => x.flow.id != selected.flow.id)]);
  };
  const removeFlow = (flowId: Flow['id']) => {
    setSelectedFlows((s) => s.filter((x) => x.flow.id != flowId));
  };

  const selectFlow = (flow: FlowWithDocument) => addFlow({ flow, split: false, amount: flowAmount(flow) });
  const splitFlow = (flow: FlowWithDocument, amount: Money) => {
    const remainingAmount = maxMoney(newMoney(0, flow.amount.currency), subtractMoney(flow.amount, amount));

    return addFlow({
      flow,
      amount: flow.document.isInflow ? amount : negateMoney(amount),
      split: true,
      remainingAmount,
    });
  };

  const getProps = (flow: Flow) => selectedFlows.find((x) => x.flow.id === flow.id);
  const isSelected = (flow: Flow) => isDefined(getProps(flow));

  const mixedCurrencies = hasMixedCurrencies(transaction.amount, ...selectedFlows.map((selected) => selected.amount));

  // ATM we are not able to sum amount with different currencies
  // because we dont have exchange rates, for this reason
  // we skip the remaining amount calc in this case
  const remainingAmount = mixedCurrencies
    ? undefined
    : selectedFlows.map((selected) => selected.amount).reduce(subtractMoney, transaction.amount);

  const value = {
    transaction,
    selectFlow,
    splitFlow,
    removeFlow,
    isSelected,
    getProps,
    selectedFlows,
    remainingAmount,
    isReconciled: isDefined(remainingAmount) && isZero(remainingAmount),
    hasSelected: !isEmpty(selectedFlows),
  };

  return <ReconcileModalContext.Provider value={value}>{children}</ReconcileModalContext.Provider>;
};

export const useReconcileModalContext = () => {
  const result = useContext(ReconcileModalContext);
  if (!result) {
    throw new Error('Context used outside of its Provider!');
  }
  return result;
};
